import React from 'react';
import { createPortal } from 'react-dom';
import { useReactTable, getCoreRowModel, flexRender, Row, Column } from '@tanstack/react-table';
import { TableProps } from 'src/components/Table/Table.types';
import { useTableStore } from 'src/components/Table/useTableStore';
import { cn, render } from 'src/lib/utils';
import { Table, TableBody, TableCell, TableRow } from 'src/components/ui/table';
import { GripHorizontal, Loader2 } from 'lucide-react';
import {
  DragDropContext,
  Draggable,
  DraggableStateSnapshot,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import {
  ComposedTableBodyEmpty,
  ComposedTableHeader,
  ComposedTableWrapper,
  TableBodySkeleton,
} from 'src/components/Table/composed-table';
import { requestClient } from 'src/lib/services/api/request-api';
import { TableColumnsVisibilityToggle } from 'src/components/Table/table-columns-visibility-toggle';
import { useBreakpoint } from 'src/lib/hooks';
import { useMutation } from '@tanstack/react-query';
import { ErrorStateWithRetry } from 'src/components/ui/error-state-with-retry';
import { shouldShowLoadError } from 'src/components/ui/error-state-with-retry.logic';
import { Button } from 'src/components/ui/button/button';
import { useTranslation } from 'react-i18next';

/** ID of the portal target element for the column toggle in the filter bar */
export const COLUMN_TOGGLE_PORTAL_ID = 'requests-column-toggle-portal';

const ComposedRequestsTable: React.FC<
  Omit<TableProps, 'enableColumnsHide'> & {
    currentTab?: string;
  }
> = ({
  columns,
  getRowCanExpand = () => false,
  expandedRowComponent,
  currentTab,
  ...props
}) => {
  const { t } = useTranslation();
  const store = useTableStore();
  const showLoadError = shouldShowLoadError({
    isError: !!store.error,
    hasData: store.data.length > 0,
  });

  // NOTE: the "show 30 before Load more" default lives in request-list.tsx
  // (setLimit(30) on mount). This table only ever renders under RequestList,
  // so a second initializer here just raced that one and triggered an extra
  // refetch that flashed the Load-more button via keepPreviousData (#2483).

  const reorderMutation = useMutation({
    mutationFn: ([sourceItemIndex, targetItemIndex, sourceRequestId, targetRequestId]: [
      number,
      number,
      string,
      string,
    ]) => {
      // mutationFn runs after onMutate, so we pass request ids gotten from original list via arguments
      return requestClient.sort(sourceRequestId, {
        request_id: targetRequestId,
        position: sourceItemIndex > targetItemIndex ? 'before' : 'after',
      });
    },
    onMutate: ([sourceIndex, targetIndex]) => {
      store.reorder(sourceIndex, targetIndex);
    },
    onError: (error, [sourceIndex, targetIndex]) => {
      store.reorder(targetIndex, sourceIndex);
    },
    onSettled: () => {
      store.refetch();
    },
  });

  /**
   * As long as the requests table tab is not "all", we are able to perform an optimistic update,
   * and we don't need wait for the refetch.
   * When showing "all" requests, because the requests are "grouped" (sorted) by status first,
   * we have to do a refetch to show correct order of requests.
   */
  const showReorderMutationLoader = currentTab === 'all' && reorderMutation.isLoading;

  const table = useReactTable({
    data: store.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    pageCount: store.totalPages,
    manualPagination: true,
    state: {
      pagination: store.pagination,
      sorting: store.sorting,
      columnVisibility: store.columnVisibility,
      expanded: store.expanded,
    },
    onColumnVisibilityChange: store.setColumnVisibility,
    onExpandedChange: store.setExpanded,
    onPaginationChange: store.setPagination,
    onSortingChange: store.setSorting,
    manualSorting: true,
    manualExpanding: true,
    paginateExpandedRows: false,
    getRowCanExpand,
  });

  async function onDragEnd(result: DropResult) {
    const { source, destination } = result;

    // dropped outside the list or canceled drag
    if (!destination || result.reason === 'CANCEL') {
      return;
    }

    if (destination.index === source.index) {
      return;
    }

    // No drag restrictions: Needs Attention / Needs Re-edit rows are placed at
    // the top once when first flagged (server-side, #2491) and are then freely
    // reorderable like any other row, so the queue order is purely the client's
    // manual `sort`. (The old "Cannot overtake…" block tied to the server's
    // query-time pin was removed with the pin.)
    reorderMutation.mutate([
      source.index,
      destination.index,
      store.data[source.index].id,
      store.data[destination.index].id,
    ]);
  }

  // Periodic refetch keeps the Last Updated relative timestamps from going
  // stale on long-idle pages — `formatRelativeTime` runs once per render, so a
  // row sitting on screen would otherwise read "2 hours ago" indefinitely.
  // 60s is a cheap cadence and also surfaces activity from other clients.
  // Guards: skip while the tab is hidden (no relative timestamps are being
  // read, polling would be wasted) and while a reorder is in flight (its
  // optimistic `store.reorder` would be overwritten by server data that
  // doesn't yet reflect the pending sort). A visibilitychange listener catches
  // the page back up the moment the user refocuses.
  const reorderInFlightRef = React.useRef(false);
  reorderInFlightRef.current = reorderMutation.isLoading;
  React.useEffect(() => {
    const tick = () => {
      if (document.hidden) return;
      if (reorderInFlightRef.current) return;
      store.refetch();
    };
    const id = window.setInterval(tick, 60_000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [store.refetch]);

  const [portalTarget, setPortalTarget] = React.useState<HTMLElement | null>(null);
  React.useEffect(() => {
    const el = document.getElementById(COLUMN_TOGGLE_PORTAL_ID);
    if (el) {
      setPortalTarget(el);
      return;
    }
    // If the portal target hasn't rendered yet, watch for it
    const observer = new MutationObserver(() => {
      const el = document.getElementById(COLUMN_TOGGLE_PORTAL_ID);
      if (el) {
        setPortalTarget(el);
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <ComposedTableWrapper className="tw-h-auto">
      {/* Column toggle is portaled to the filter bar via COLUMN_TOGGLE_PORTAL_ID */}
      {portalTarget &&
        createPortal(
          <RequestsTableColumnToggle
            columns={table.getAllLeafColumns()}
            currentTab={currentTab}
          />,
          portalTarget,
        )}

      <DragDropContext
        onDragEnd={onDragEnd}
        onBeforeCapture={() => {
          // collapse all expanded rows
          store.setExpanded({});
        }}
      >
        <Table
          wrapperProps={{
            // Bounded height makes the table its own scroll container — the parent
            // Radix ScrollArea has no horizontal bar, so without this the rightmost
            // columns get clipped on narrower viewports. Buffer covers the
            // AuthenticatedHeader (~60px max-h). The Load-more button lives inside
            // this wrapper (wrapperChildren) so it scrolls with the rows instead of
            // sitting pinned below the scroll area (Karina #2483).
            className:
              'tw-border-0 tw-rounded-none tw-min-h-[300px] tw-max-h-[calc(100vh-var(--filter-bar-h,52px)-var(--verification-bar-h,0px)-100px)]',
          }}
          className={'tw-relative tw-border-spacing-y-1.5'}
          wrapperChildren={
            // TODO: Switch to offset pagination (keep pageSize=30, increment
            // pageIndex, accumulate results) so each click only fetches 30 new
            // items instead of re-fetching the growing dataset.
            //
            // Only show Load-more once a *full* page is loaded. If the server
            // returned fewer rows than the requested pageSize there is nothing
            // left to fetch, so the button must stay hidden regardless of what
            // `totalItems` says. This makes the button impossible to show when
            // the count is under 30 (e.g. 18 rows < pageSize 30) and also kills
            // the keepPreviousData stale-window flash on submit/resubmit, where
            // a smaller previous page briefly satisfied `length < totalItems`
            // (Karina #2483 re-open).
            store.data.length >= store.pagination.pageSize &&
            store.data.length < store.totalItems ? (
              // tw-sticky tw-left-0 keeps the centered button in view when the
              // table scrolls horizontally on narrow viewports.
              <div className="tw-sticky tw-left-0 tw-flex tw-justify-center tw-py-4">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  className="tw-min-w-[200px]"
                  disabled={store.isLoading || store.isFetching}
                  onClick={() => {
                    const currentSize = table.getState().pagination.pageSize;
                    store.setPagination((prev) => ({
                      ...prev,
                      pageIndex: 0,
                      pageSize: currentSize + 30,
                    }));
                  }}
                >
                  {store.isFetching ? (
                    <Loader2 className="tw-mr-2 tw-size-4 tw-animate-spin" />
                  ) : null}
                  {t('actions:load_more', { defaultValue: 'Load more' })}
                </Button>
              </div>
            ) : null
          }
        >
          <ComposedTableHeader
            table={table}
            className="tw-z-10 [&_th]:tw-h-9 [&_th]:tw-py-0.5 [&_th]:tw-text-xs [&_th_button]:tw-py-0.5"
          />

          <Droppable droppableId={'requests-droppable'}>
            {(provided) => (
              <TableBody
                ref={provided.innerRef}
                {...provided.droppableProps}
                // Admin's 8px row corners (#2756); only the rows, not every
                // control inside them.
                className={
                  '[&_td:first-child]:tw-rounded-bl-[0.5rem] [&_td:first-child]:tw-rounded-tl-[0.5rem] [&_td:last-child]:tw-rounded-br-[0.5rem] [&_td:last-child]:tw-rounded-tr-[0.5rem]'
                }
              >
                {showLoadError ? (
                  <tr>
                    <td colSpan={99}>
                      <ErrorStateWithRetry onRetry={() => store.refetch()} />
                    </td>
                  </tr>
                ) : !store.isLoading && !table.getRowModel().rows.length ? (
                  <ComposedTableBodyEmpty />
                ) : null}
                {store.isLoading ? (
                  <TableBodySkeleton columnCount={table.getVisibleLeafColumns().length} />
                ) : (
                  <>
                    <ComposedRequestsTableRows
                      rows={table.getRowModel().rows}
                      tableRowProps={props.tableRowProps}
                      expandedRowComponent={expandedRowComponent}
                      currentTab={currentTab}
                    />
                    {showReorderMutationLoader && (
                      <tr
                        className={
                          'tw-absolute tw-inset-0 tw-z-10 tw-animate-pulse tw-bg-neutral-200/70'
                        }
                      >
                        <td colSpan={99} className={'tw-block tw-h-full tw-w-full !tw-border-0'}>
                          <div
                            className={
                              'tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center'
                            }
                          >
                            <Loader2 className={'tw-size-12 tw-animate-spin tw-text-brand'} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </Table>
      </DragDropContext>
    </ComposedTableWrapper>
  );
};

// Tabs where users can reorder requests via drag-and-drop.
// "all" mixes statuses together so reordering is ambiguous; "draft"/"uploading"
// have no queue meaning; and per #2392 Editing / Ready-for-Review / Approved
// default-sort by last_activity_at DESC, so manual rank no longer drives their
// order — leaving drag enabled there would produce a reorder that's invisible
// after the next refetch.
const DRAG_ENABLED_TABS = ['queued'];
// Tabs where a 1..N rank is displayed on each row. Same rationale as above —
// rank only means anything when the underlying order is the manual queue.
const RANK_VISIBLE_TABS = ['queued'];

const ComposedRequestsTableRows: React.FC<{
  rows: Row<any>[];
  tableRowProps: TableProps['tableRowProps'];
  expandedRowComponent: TableProps['expandedRowComponent'];
  currentTab?: string;
}> = React.memo(({ rows, tableRowProps, expandedRowComponent, currentTab }) => {
  const store = useTableStore();
  const { isMobile } = useBreakpoint();

  const isDragAllowedInTab = !!currentTab && DRAG_ENABLED_TABS.includes(currentTab);
  const showRank = !!currentTab && RANK_VISIBLE_TABS.includes(currentTab);
  const isDragDisabled = !!store.sorting.length || !isDragAllowedInTab;

  function getDraggableStyle(
    style: React.CSSProperties | undefined,
    snapshot: DraggableStateSnapshot,
  ) {
    if (snapshot.isDropAnimating && snapshot.dropAnimation) {
      // adjust translate position to account for table row spacing
      const { moveTo } = snapshot.dropAnimation;
      // minus table border-spacing-3 (0.75rem = 12px) times 2
      const translate = `translate(${moveTo.x}px, ${moveTo.y - 24}px)`;

      return {
        ...style,
        display: 'table', // needed to support draggable table row
        transform: translate,
      };
    }

    if (snapshot.isDragging) {
      return {
        ...style,
        display: 'table',
      };
    }

    return style;
  }

  return (
    <>
      {rows.map((row) => {
        const { className, active, ...rowProps } = tableRowProps?.(row) ?? {};
        const canExpand = row.getCanExpand();
        const isExpanded = row.getIsExpanded();

        return (
          <React.Fragment key={row.id}>
            <Draggable draggableId={row.id} index={row.index} isDragDisabled={isDragDisabled}>
              {(provided, snapshot) => (
                <TableRow
                  active={canExpand && isExpanded ? true : active}
                  className={cn('tw-group/row', canExpand && 'tw-peer', className)}
                  ref={provided.innerRef}
                  {...rowProps}
                  {...provided.draggableProps}
                  style={getDraggableStyle(provided.draggableProps.style, snapshot)}
                >
                  {row.getVisibleCells().map((cell, i) => {
                    const cellContext = cell.getContext();
                    const isLastColumn = row.getVisibleCells().length - 1 === i;
                    const visibleColumnCount = row.getVisibleCells().length;
                    // on mobile, whole table cell is draggable. Render a separate drag handle component on bigger screens
                    const cellProps = isMobile ? { ...provided.dragHandleProps } : {};
                    const showHandle = !isMobile && !isDragDisabled;
                    // Always-visible rank+grip slot is reserved for tabs that show a rank
                    // (Priority Queue etc.). On tabs that allow drag without showing rank
                    // (e.g. Approved), keep the original hover-only grip with no padding
                    // shift to avoid a visual regression on every row.
                    const showRowSlot = i === 0 && showRank;
                    const showHoverHandle = i === 0 && !showRank && showHandle;

                    return (
                      <TableCell
                        key={cell.id}
                        className={cn('tw-relative', {
                          'tw-text-end': isLastColumn && visibleColumnCount > 1,
                          'tw-pl-12': showRowSlot && showHandle,
                          'tw-pl-7': showRowSlot && !showHandle,
                        })}
                        {...cellProps}
                      >
                        {showRowSlot && (
                          <div
                            className={cn(
                              'tw-absolute tw-left-1 tw-top-1/2 -tw-translate-y-1/2 tw-flex tw-items-center tw-gap-1',
                              snapshot.isDragging && 'tw-hidden',
                            )}
                          >
                            <span
                              className={
                                'tw-pointer-events-none tw-text-xs tw-font-medium tw-text-neutral-500 tw-tabular-nums'
                              }
                            >
                              {row.index + 1}
                            </span>
                            {showHandle && (
                              <div
                                className={'tw-cursor-grab'}
                                {...provided.dragHandleProps}
                              >
                                <GripHorizontal
                                  size={16}
                                  className={'tw-rotate-90 tw-text-neutral-400'}
                                />
                              </div>
                            )}
                          </div>
                        )}
                        {showHoverHandle && (
                          <div
                            className={cn(
                              'tw-absolute tw-left-0.5 tw-top-1/2 tw-hidden -tw-translate-y-1/2 tw-cursor-grab group-hover/row:tw-block',
                              snapshot.isDragging && 'tw-block',
                            )}
                            {...provided.dragHandleProps}
                          >
                            <GripHorizontal
                              size={16}
                              className={'tw-rotate-90 tw-text-neutral-400'}
                            />
                          </div>
                        )}
                        {flexRender(cell.column.columnDef.cell, {
                          ...cellContext,
                          helpers: {
                            refetch: store.refetch,
                            setData: store.setData,
                            pagination: store.pagination,
                            setPagination: store.setPagination,
                          },
                          row: {
                            ...cellContext.row,
                            edit: store.getEditRow(cellContext.row.index),
                          },
                          rows: store.data,
                        })}
                      </TableCell>
                    );
                  })}
                </TableRow>
              )}
            </Draggable>
            {canExpand && isExpanded ? render(expandedRowComponent, row) : null}
          </React.Fragment>
        );
      })}
    </>
  );
});
ComposedRequestsTableRows.displayName = 'ComposedRequestsTableRows';

// Tabs whose default column set swaps Created At for Last Updated (#2385 covers
// 'all'; #2392 extends to the three in-flight status tabs). Queued / Draft /
// Uploading keep Created At visible by default since they don't sort by
// activity. Users can still re-add either column via the Columns toggle.
const ACTIVITY_DEFAULTED_TABS = ['all', 'currently_editing', 'delivered', 'complete'];

const RequestsTableColumnToggle: React.FC<{
  columns: Column<any, any>[];
  currentTab?: string;
}> = ({ columns, currentTab }) => {
  const { isMobile } = useBreakpoint();

  const toggleableColumns = React.useMemo(() => {
    // do not allow to hide first and last table columns
    const toggleableColumns = [...columns];
    toggleableColumns.shift();
    toggleableColumns.pop();

    return toggleableColumns;
  }, [columns]);

  // Apply per-breakpoint defaults:
  // - Mobile: only the first column is visible.
  // - Desktop: the date-column default flips per tab — activity-defaulted tabs
  //   (see ACTIVITY_DEFAULTED_TABS) hide created_at and show last_activity_at;
  //   other tabs do the inverse. The `priority` column always defaults to
  //   hidden because drag-and-drop reordering makes the Low/High label
  //   redundant. A manual re-enable via the Columns toggle is per-session —
  //   switching tabs or remounting resets it back to hidden.
  React.useLayoutEffect(() => {
    if (isMobile) {
      columns[0].toggleVisibility(true);
      columns.slice(1).forEach((col) => col.toggleVisibility(false));
      return;
    }

    const isActivityDefaulted = !currentTab || ACTIVITY_DEFAULTED_TABS.includes(currentTab);
    columns.forEach((col) => {
      if (col.id === 'created_at') {
        col.toggleVisibility(!isActivityDefaulted);
        return;
      }
      if (col.id === 'last_activity_at') {
        col.toggleVisibility(isActivityDefaulted);
        return;
      }
      if (col.id === 'priority') {
        col.toggleVisibility(false);
        return;
      }
      col.toggleVisibility(true);
    });
  }, [isMobile, currentTab]);

  // One-shot cleanup of the legacy persistence key from before #2395 — the
  // value was re-applied as a sticky default which leaked the column across
  // tabs. Safe to drop after this change has been deployed long enough that
  // every active user has run it once.
  React.useEffect(() => {
    try {
      window.localStorage.removeItem('requests-table:priority-column-visible');
    } catch {
      // ignore — storage may be unavailable
    }
  }, []);

  if (!toggleableColumns.length) {
    return null;
  }

  return (
    <TableColumnsVisibilityToggle
      columns={toggleableColumns as any}
      menuTriggerProps={{
        className: 'tw-hidden md:tw-inline-flex',
      }}
      getIsAllColumnsVisible={() => {
        return toggleableColumns.every((col) => col.getIsVisible());
      }}
      getIsSomeColumnsVisible={() => toggleableColumns.some((col) => col.getIsVisible())}
      getToggleAllColumnsVisibilityHandler={() => (checked) => {
        toggleableColumns.forEach((col) => col.toggleVisibility(checked as boolean));
      }}
    />
  );
};

export { ComposedRequestsTable };
